import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { CostChangePct } from "@/accounts-payable/common";
import {
  AP_ACTION_TYPE,
  type ApAgentAction,
  type ApPoLineItem,
} from "@/accounts-payable/types";
import {
  formatApDate,
  formatDollars,
  receiptReference,
  stJobUrl,
  stPricebookItemUrl,
  stProjectUrl,
  stPurchaseOrderUrl,
} from "@/accounts-payable/utils";
import { LineItemList } from "./line-item-list";
import { QboBillRecord } from "./qbo-bill-record";
import {
  StLink,
  StRecord,
  RecordField,
  RecordFieldGrid,
  NoStCard,
} from "./record-fields";
import { sentenceCase, getPoRecordFields } from "./utils";

/** Whether ServiceTitan bills a material to the customer by default. */
const chargeableLabel = (chargeable: boolean) => (chargeable ? "Yes" : "No");
/** What ServiceTitan object an action targets, with links + current/proposed context. */
export const TargetObject = ({ action }: { action: ApAgentAction }) => {
  const p = action.payload;
  const jobLink = (id: number) => (
    <StLink url={stJobUrl(id)} label={`Open job #${id} in ServiceTitan`}>
      Job #{id}
    </StLink>
  );
  const projectLink = (id: number) => (
    <StLink
      url={stProjectUrl(id)}
      label={`Open project #${id} in ServiceTitan`}
    >
      Project #{id}
    </StLink>
  );
  const poLink = (id: number) => (
    <StLink
      url={stPurchaseOrderUrl(id)}
      label={`Open purchase order #${id} in ServiceTitan`}
    >
      PO #{id}
    </StLink>
  );
  const vendorName = (
    <Text>
      {p.st_vendor_name} <span className="text-subtle">#{p.st_vendor_id}</span>
    </Text>
  );

  switch (action.action_type) {
    case AP_ACTION_TYPE.matchJob:
      return p.st_project_external_id ? (
        <StRecord
          label="Project"
          link={projectLink(p.st_project_external_id)}
        />
      ) : (
        <StRecord label="Job" link={jobLink(p.st_job_external_id)} />
      );
    case AP_ACTION_TYPE.linkVendor:
      return <StRecord label="Vendor" name={vendorName} />;
    case AP_ACTION_TYPE.mergeVendor:
      return (
        <StRecord label="Vendor" name={vendorName}>
          {/* Names the vendor as it appears in ST (`st_vendor_name`), not
              the "display name" of the surviving Volca record
              (`target_vendor_name`); a display name that looks "wrong"
              would only invite rejecting a functionally correct merge. */}
          <Text tone="muted">
            Volca already has a vendor record linked to {p.st_vendor_name}. This
            invoice's vendor "{p.source_vendor_name}" is merged into that record
            and kept as an alias, so future invoices under that name still
            match. ServiceTitan is not changed.
          </Text>
        </StRecord>
      );
    case AP_ACTION_TYPE.updatePricebookCost:
      return (
        <StRecord
          label={`Pricebook ${p.pricebook_item_type ?? "item"}`}
          link={
            <StLink
              url={stPricebookItemUrl(
                p.pricebook_item_type,
                p.pricebook_item_external_id,
              )}
              label={`Open ${p.sku} in ServiceTitan`}
            >
              <span className="font-mono">{p.sku}</span>
            </StLink>
          }
        >
          <RecordFieldGrid>
            {p.proposed_cost != null && (
              <>
                <RecordField label="Current cost">
                  {formatDollars(p.current_cost)}
                </RecordField>
                <RecordField label="Recommended cost" className="col-span-2">
                  <Stack direction="row" gap={2} align="baseline">
                    <Text>{formatDollars(p.proposed_cost)}</Text>
                    <CostChangePct
                      current={p.current_cost}
                      proposed={p.proposed_cost}
                      className="text-sm"
                    />
                  </Stack>
                </RecordField>
              </>
            )}
            {p.chargeable != null && (
              <RecordField label="Chargeable" className="col-span-3">
                {p.current_chargeable == null
                  ? chargeableLabel(p.chargeable)
                  : `${chargeableLabel(p.current_chargeable)} → ${chargeableLabel(p.chargeable)}`}
              </RecordField>
            )}
          </RecordFieldGrid>
        </StRecord>
      );
    case AP_ACTION_TYPE.createPricebookItem:
      return (
        <StRecord label="pricebook item" isNew name={<Text>{p.name}</Text>}>
          {p.description && (
            <RecordField label="Description">{p.description}</RecordField>
          )}
          <RecordFieldGrid>
            <RecordField label="Code">{p.code}</RecordField>
            <RecordField label="Type">{sentenceCase(p.item_type)}</RecordField>
            <RecordField label="Cost">{formatDollars(p.cost)}</RecordField>
            {p.chargeable != null && (
              <RecordField label="Chargeable">
                {chargeableLabel(p.chargeable)}
              </RecordField>
            )}
          </RecordFieldGrid>
        </StRecord>
      );
    case AP_ACTION_TYPE.updatePo: {
      const { short, wide, items } = getPoRecordFields(p.changes);
      return (
        <StRecord label="Purchase order" link={poLink(p.st_purchase_order_id)}>
          {short.length > 0 && (
            <RecordFieldGrid>
              {short.map((field) => (
                <RecordField key={field.key} label={field.label}>
                  {field.value}
                </RecordField>
              ))}
            </RecordFieldGrid>
          )}
          {wide.map((field) => (
            <RecordField key={field.key} label={field.label}>
              {field.value}
            </RecordField>
          ))}
          {/* The apply keeps the PO's summary and appends the links itself,
              so a proposed memo isn't written over one (ENG-2883). */}
          <Text tone="muted">
            The PO&apos;s summary stays as it is, with links to this document
            added below it.
            {"memo" in p.changes &&
              " The memo is only used if the summary is blank."}
          </Text>
          {items && items.length > 0 && <LineItemList items={items} />}
          {p.reason && <RecordField label="Reason">{p.reason}</RecordField>}
        </StRecord>
      );
    }
    case AP_ACTION_TYPE.createPo:
    case AP_ACTION_TYPE.createReturn: {
      const items = (p.items ?? []) as ApPoLineItem[];
      return (
        <StRecord
          label={
            action.action_type === AP_ACTION_TYPE.createPo
              ? "purchase order"
              : "return"
          }
          isNew
          link={
            p.st_purchase_order_id ? poLink(p.st_purchase_order_id) : undefined
          }
        >
          <RecordField label={p.st_project_external_id ? "Project" : "Job"}>
            {p.st_job_external_id ? (
              jobLink(p.st_job_external_id)
            ) : p.st_project_external_id ? (
              /* Filed on the project itself, with no job. */
              projectLink(p.st_project_external_id)
            ) : (
              /* A non-job purchase: ServiceTitan files it with no job, so
                 there is nothing to link to here. */
              <Text>No job (non-job expense)</Text>
            )}
          </RecordField>
          {p.reason && <Text tone="muted">{p.reason}</Text>}
          {items.length > 0 && <LineItemList items={items} />}
        </StRecord>
      );
    }
    case AP_ACTION_TYPE.createReceipt:
    case AP_ACTION_TYPE.replaceReceipt: {
      const items = (p.items ?? []) as ApPoLineItem[];
      return (
        <StRecord label="receipt" isNew link={poLink(p.st_purchase_order_id)}>
          {action.action_type === AP_ACTION_TYPE.replaceReceipt && (
            /* A replacement voids the old receipt first, which a reviewer
               must not miss behind a "new receipt" header. */
            <Text tone="muted">
              Cancels receipt {receiptReference(p)} and records these lines in
              its place. {p.reason}
            </Text>
          )}
          <RecordFieldGrid>
            <RecordField label="Received">
              {p.date
                ? formatApDate(p.date)
                : action.action_type === AP_ACTION_TYPE.replaceReceipt
                  ? "Same as the old receipt"
                  : "Document date"}
            </RecordField>
            <RecordField label="Tax">{formatDollars(p.tax ?? 0)}</RecordField>
            <RecordField label="Shipping">
              {formatDollars(p.shipping ?? 0)}
            </RecordField>
          </RecordFieldGrid>
          {p.vendor_document_number && (
            <RecordField label="Filed under vendor document">
              {p.vendor_document_number}
            </RecordField>
          )}
          {items.length > 0 && <LineItemList items={items} />}
        </StRecord>
      );
    }
    case AP_ACTION_TYPE.cancelReceipt:
      return (
        <StRecord label="Receipt" name={<Text>{receiptReference(p)}</Text>}>
          <RecordField label="Purchase order">
            {poLink(p.st_purchase_order_id)}
          </RecordField>
          <Text tone="muted">Cancels this receipt. {p.reason}</Text>
        </StRecord>
      );
    case AP_ACTION_TYPE.markNonJob:
      return p.st_purchase_order_id ? (
        <StRecord label="purchase order" link={poLink(p.st_purchase_order_id)}>
          <Text tone="muted">
            Already files this invoice's cost with no job. {p.reason}
          </Text>
        </StRecord>
      ) : (
        <NoStCard reason={p.reason} />
      );
    case AP_ACTION_TYPE.createQboBill:
    case AP_ACTION_TYPE.updateQboBill:
      return <QboBillRecord payload={p} />;
    case AP_ACTION_TYPE.flagForReview:
    case AP_ACTION_TYPE.noAction:
      // The row's title already is the reason; nothing touches ServiceTitan.
      return null;
    default:
      return null;
  }
};
